import {
	activeFocuses as activeFocusesOf,
	backlinks as backlinksOf,
	comparePositions,
	containerEntries,
	dayNodeId,
	focuses as focusesOf,
	isSystemRole,
	isUserVisible,
	type OutlineEntry,
	type OverdueEntry,
	overdue as overdueOf,
	type PlainDate,
	projects as projectsOf,
	type RecurringEntry,
	recurringOn as recurringOnOf,
	SYSTEM_NODE_IDS,
	scheduledOn as scheduledOnOf,
	type TagColor
} from "@mapcraft-tasks/domain";
import { containerRows, type Replica } from "./replica";
import type { Node } from "./types";

/**
 * Выборки считаются ЛОКАЛЬНО, теми же чистыми функциями, что и на сервере
 * (`packages/domain/views.ts`). Серверные `core/views` остаются REST/MCP-агенту —
 * у него локального стора нет. Стороны разъехаться не могут: функция одна.
 */

export type { OutlineEntry, OverdueEntry, RecurringEntry };

const nodesOf = (replica: Replica) => [...replica.nodes.values()];

export function getNode(replica: Replica, id: string): Node | undefined {
	return replica.nodes.get(id);
}

export function children(replica: Replica, containerId: string): OutlineEntry<Node>[] {
	return containerEntries(nodesOf(replica), [...replica.placements.values()], containerId);
}

/** Есть ли что раскрывать: считается по строкам контейнера, а не по `parent_id`. */
export function hasChildren(replica: Replica, containerId: string): boolean {
	return containerRows(replica, containerId).length > 0;
}

/**
 * Today — это ОБЫЧНАЯ страница day-ноды. Просмотр день не создаёт: `ensureDay` дёргается
 * только на первую запись, иначе Календарь засорялся бы пустыми днями. Нет ноды — пусто.
 */
export function dayPage(replica: Replica, date: PlainDate): OutlineEntry<Node>[] {
	return children(replica, dayNodeId(date));
}

export function scheduledOn(replica: Replica, date: PlainDate): Node[] {
	return scheduledOnOf(nodesOf(replica), date);
}

export function recurringOn(replica: Replica, date: PlainDate): RecurringEntry<Node>[] {
	return recurringOnOf(nodesOf(replica), [...replica.completions.values()], date);
}

export function inbox(replica: Replica): OutlineEntry<Node>[] {
	return children(replica, SYSTEM_NODE_IDS.inbox);
}

export function pinned(replica: Replica): OutlineEntry<Node>[] {
	return children(replica, SYSTEM_NODE_IDS.pinned);
}

/** Панель фокуса: открытые заходы. Фокус — роль ноды, контейнера у него нет. */
export function activeFocuses(replica: Replica): Node[] {
	return activeFocusesOf(nodesOf(replica));
}

/** Страница `/focus`: открытые сверху, закрытые под ними — свежие первыми. */
export function focuses(replica: Replica): Node[] {
	return focusesOf(nodesOf(replica));
}

export function projects(replica: Replica): Node[] {
	return projectsOf(nodesOf(replica));
}

export function overdue(replica: Replica, today: PlainDate): OverdueEntry<Node>[] {
	return overdueOf(nodesOf(replica), [...replica.placements.values()], today);
}

export function backlinks(replica: Replica, nodeId: string): Node[] {
	return backlinksOf(nodesOf(replica), [...replica.links.values()], nodeId);
}

/** Свободные области — дети root, кроме системных контейнеров (Work, Personal…). */
export function areas(replica: Replica): Node[] {
	return nodesOf(replica)
		.filter((n) => n.parentId === SYSTEM_NODE_IDS.root && !isSystemRole(n.role))
		.sort(comparePositions);
}

/** Календарь: дни, свежие сверху. */
export function days(replica: Replica): Node[] {
	return nodesOf(replica)
		.filter((n) => n.role === "day" && n.dayDate !== null)
		.sort((a, b) => (a.dayDate! < b.dayDate! ? 1 : -1));
}

export function allRecurring(replica: Replica): Node[] {
	return nodesOf(replica)
		.filter((n) => n.role === "recurring" && n.archivedAt === null)
		.sort(comparePositions);
}

/**
 * Поиск — перебором по локальной реплике. 20k нод перебираются мгновенно; ни `tsvector`,
 * ни локальный индекс не нужны, пока объём не вырос (ui-spec Область 10).
 */
export function search(replica: Replica, query: string, limit = 50): Node[] {
	const q = query.trim().toLowerCase();
	if (q === "") {
		return [];
	}
	const out: Node[] = [];
	for (const node of replica.nodes.values()) {
		if (!isUserVisible(node) || node.archivedAt !== null) {
			continue;
		}
		if (node.title.toLowerCase().includes(q) || (node.body?.toLowerCase().includes(q) ?? false)) {
			out.push(node);
			if (out.length >= limit) {
				break;
			}
		}
	}
	return out;
}

/**
 * Все теги живой части реплики с числом нод — источник подсказок в редакторе тегов.
 * Частые идут первыми: тег, которым помечено полсотни нод, нужен чаще разового.
 * Архив не считается — его в реплике и нет, а тег, оставшийся только там, подсказывать незачем.
 */
export function allTags(replica: Replica): { tag: string; count: number }[] {
	const counts = new Map<string, number>();
	for (const node of replica.nodes.values()) {
		if (node.archivedAt !== null) {
			continue;
		}
		for (const tag of node.tags) {
			counts.set(tag, (counts.get(tag) ?? 0) + 1);
		}
	}
	return [...counts]
		.map(([tag, count]) => ({ tag, count }))
		.sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag, "ru"));
}

/** Цвет по имени тега. Тега нет в карте — он нейтральный. */
export function tagColors(replica: Replica): ReadonlyMap<string, TagColor> {
	const out = new Map<string, TagColor>();
	for (const tag of replica.tags.values()) {
		out.set(tag.name, tag.color);
	}
	return out;
}

/** Хлебные крошки от корня к ноде, не включая её саму. */
export function breadcrumbs(replica: Replica, nodeId: string): Node[] {
	const chain: Node[] = [];
	const seen = new Set<string>([nodeId]);
	let cursor = replica.nodes.get(nodeId)?.parentId ?? null;
	while (cursor && !seen.has(cursor)) {
		seen.add(cursor);
		const node = replica.nodes.get(cursor);
		if (!node) {
			break;
		}
		chain.push(node);
		cursor = node.parentId;
	}
	return chain.reverse();
}

/** Контейнеры, куда нода вплетена, — панель «где ещё показывается». */
export function placementsOf(replica: Replica, nodeId: string): { placementId: string; container: Node }[] {
	const out: { placementId: string; container: Node }[] = [];
	for (const placement of replica.placements.values()) {
		if (placement.nodeId !== nodeId) {
			continue;
		}
		const container = replica.nodes.get(placement.containerId);
		if (container) {
			out.push({ placementId: placement.id, container });
		}
	}
	return out;
}

function isPlacedIn(replica: Replica, nodeId: string, containerId: string): boolean {
	for (const placement of replica.placements.values()) {
		if (placement.nodeId === nodeId && placement.containerId === containerId) {
			return true;
		}
	}
	return false;
}

export function isPinned(replica: Replica, nodeId: string): boolean {
	return isPlacedIn(replica, nodeId, SYSTEM_NODE_IDS.pinned);
}
